import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import IconButton from "@mui/material/IconButton";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import { useTheme } from "@mui/material/styles";
import {
    CategoryHeaderCell,
    CategoryHeaderBox,
    CategoryName,
    CategoryStats,
    CategoryExpandCell,
    CloseIconStyled,
    CategoryStatusIcon,
    DataStatusIcon
} from './Validations.styles';
import {
    columnWidths,
    getValidationStatus,
    getCategoryColor,
    getStatusIcon,
    getStatusColor
} from './validationUtils';

const CategoryRow = React.memo(function CategoryRow({
    category,
    stats,
    isFirst,
    expanded,
    onToggle,
    theme
}) {
    // console.log('stats in CategoryRow', stats);
    return (
        <TableRow>
            <CategoryExpandCell isFirst={isFirst}>
                <IconButton size="small" onClick={onToggle} data-testid={`expand-${category}`}>
                    {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                </IconButton>
            </CategoryExpandCell>
            <CategoryHeaderCell colSpan={5} isFirst={isFirst}>
                <CategoryHeaderBox color={getCategoryColor(theme, stats.failed)} data-testid={`category-header-${category}`}>
                    <CategoryName color={getCategoryColor(theme, stats.failed)} data-testid={`category-name-${category}`}>
                        {category}
                    </CategoryName>
                    <CategoryStats color={getCategoryColor(theme, stats.failed)}>
                        {stats.passed}/{stats.applicable}
                        {stats.failed > 0 && <> ({stats.failed} failed)</>}
                        {stats.na > 0 && <> ({stats.na} N/A)</>}
                    </CategoryStats>
                    <CategoryStatusIcon color={getCategoryColor(theme, stats.failed)}>
                        {stats.failed > 0
                            ? <ClearIcon fontSize="medium" />
                            : <CheckIcon fontSize="medium" />
                        }
                    </CategoryStatusIcon>
                </CategoryHeaderBox>
            </CategoryHeaderCell>
        </TableRow>
    );
});

const DataRow = React.memo(function DataRow({ v, theme }) {
    const status = getValidationStatus(v.passed);
    const statusColor = getStatusColor(theme, status);

    return (
        <TableRow>
            <TableCell />
            <TableCell sx={{ width: columnWidths.status }}>
                <DataStatusIcon color={statusColor}>
                    {getStatusIcon(status)}
                </DataStatusIcon>
            </TableCell>
            <TableCell sx={{ width: columnWidths.field }}>{v.field_name}</TableCell>
            <TableCell sx={{ width: columnWidths.tab }}>{v.tab}</TableCell>
            <TableCell sx={{ width: columnWidths.row }}>{v.line || v.row || 'N/A'}</TableCell>
            <TableCell sx={{ width: columnWidths.rule }}>
                {status === 'na' ? 'Not applicable to this quote' : v.rule}
            </TableCell>
        </TableRow>
    );
});

const ValidationsDialog = ({
    open,
    onClose,
    sortedCategories,
    expanded,
    onToggle,
    title = "Validation Details"
}) => {
    const theme = useTheme();
    // console.log('sortedCategories in ValidationsDialog', sortedCategories);
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: { minWidth: 1200 }
            }}
        >
            <DialogTitle sx={{ fontSize: 22, pb: 1 }}>
                {title}
                <CloseIconStyled onClick={onClose} data-testid="dialog-close" />
            </DialogTitle>
            <DialogContent sx={{ pt: 0, pb: 3 }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 48 }} />
                                <TableCell sx={{ width: columnWidths.status, fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ width: columnWidths.field, fontWeight: 700 }}>Field Name</TableCell>
                                <TableCell sx={{ width: columnWidths.tab, fontWeight: 700 }}>Tab</TableCell>
                                <TableCell sx={{ width: columnWidths.row, fontWeight: 700 }}>Row</TableCell>
                                <TableCell sx={{ width: columnWidths.rule, fontWeight: 700 }}>Rule</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedCategories.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                                        No validation data available
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedCategories.map(({ category, items, stats }, catIdx) => (
                                    <React.Fragment key={category}>
                                        <CategoryRow
                                            category={category}
                                            stats={stats}
                                            isFirst={catIdx === 0}
                                            expanded={!!expanded[category]}
                                            onToggle={() => onToggle(category)}
                                            theme={theme}
                                        />
                                        {expanded[category] && items.map((v, idx) => (
                                            <DataRow 
                                                key={`${category}-${idx}`} 
                                                v={v} 
                                                theme={theme} 
                                            />
                                        ))}
                                    </React.Fragment>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </Dialog>
    );
};

export default ValidationsDialog;
